import { CompGroup } from "userful-chronos-app-common-js/dist/models/group/Groups";
import {
    ClearanceLevelID,
    EMPTY_USERFUL_SECURITY_DATA,
    UserClearanceLevelID,
    UserfulSecurityData,
} from "userful-chronos-app-common-js/dist/models/common";
import { requestUpdateGroup } from "userful-chronos-app-common-js/dist/models/group/messages/GroupMsgSender";
import { groupStore } from "userful-chronos-common-store/dist/group";
import {
    playlistSliceActions,
    sourceAssetStore,
    sourceSliceActions,
} from "userful-chronos-common-store/dist/sourceassets";
import {
    SamSourceDef,
    SamSourceDefCreator,
    createUpdateDataFromSourceDef,
} from "userful-chronos-app-common-js/dist/models/sam/SAMSource";
import { SamPlayListDef } from "userful-chronos-app-common-js/dist/models/sam/SAMPlaylist";

export function generatedUniqueName(list: string[], prefix?: string, suffix?: string, value?: string): string {
    let name = prefix + " " + suffix + (value ? " (" + value + ")" : "");
    let i = 1;

    while (list.includes(name)) {
        name = prefix + " " + suffix + " (" + i + ")";
        i++;
    }

    return name;
}

const cloneIdArray = <T extends { value: string }>(ids?: T[]): T[] => {
    return (ids ?? []).map((id) => ({ ...id }));
};

const cloneClearancePermissions = (permissions?: ClearanceLevelID[]): ClearanceLevelID[] =>
    cloneIdArray<ClearanceLevelID>(permissions);

const cloneUserClearancePermissions = (permissions?: UserClearanceLevelID[]): UserClearanceLevelID[] =>
    cloneIdArray<UserClearanceLevelID>(permissions);

const mergeClearancePermissions = (
    existing: ClearanceLevelID[] = [],
    required: ClearanceLevelID[] = [],
): ClearanceLevelID[] => {
    const merged = cloneClearancePermissions(existing);
    const seen = new Set(merged.map((permission) => permission.value));

    required.forEach((permission) => {
        if (!permission?.value || seen.has(permission.value)) {
            return;
        }
        merged.push({ value: permission.value });
        seen.add(permission.value);
    });

    return merged;
};

const createSecurityDataWithPermissions = (
    data: UserfulSecurityData | undefined,
    permissions: ClearanceLevelID[],
): UserfulSecurityData => {
    const base = data ? { ...data } : EMPTY_USERFUL_SECURITY_DATA();
    const combinedPermissions = mergeClearancePermissions(base.clearanceLevelPermissions, permissions);
    return {
        ...base,
        clearanceLevelPermissions: combinedPermissions,
        userClearanceLevelPermissions: cloneUserClearancePermissions(base.userClearanceLevelPermissions),
        securityDataSet: combinedPermissions.length > 0 || base.securityDataSet,
    };
};

const buildGroupUpdate = (group: CompGroup, permissions: ClearanceLevelID[]): CompGroup => ({
    ...group,
    userfulSecurityData: createSecurityDataWithPermissions(group.userfulSecurityData, permissions),
});

const buildPlaylistUpdate = (playlist: SamPlayListDef, permissions: ClearanceLevelID[]): SamPlayListDef => ({
    ...playlist,
    userfulSecurityData: createSecurityDataWithPermissions(playlist.userfulSecurityData, permissions),
});

const buildSourceUpdate = (source: SamSourceDef, permissions: ClearanceLevelID[]): SamSourceDefCreator => {
    const payload = createUpdateDataFromSourceDef(source);
    payload.userfulSecurityData = createSecurityDataWithPermissions(source.userfulSecurityData, permissions);
    return payload;
};

const getGroupState = () => {
    try {
        return groupStore.getState();
    } catch (error) {
        console.warn("Group store not initialized", error);
        return null;
    }
};

const getSourceAssetState = () => {
    try {
        return sourceAssetStore.getState();
    } catch (error) {
        console.warn("Source asset store not initialized", error);
        return null;
    }
};

const getChildById = <T extends { id: { value: string } }>(map: Map<string, T>, id: string): T | undefined =>
    map.get(id);

export const recursivellyUpdateAllPermissionsWithinFolder = (folder: CompGroup) => {
    if (!folder || !folder.comps || folder.comps.length === 0) {
        return;
    }

    const groupState = getGroupState();
    const sourceAssetState = getSourceAssetState();

    if (!groupState || !sourceAssetState) {
        return;
    }

    const groupsById = new Map(groupState.group.groups.map((group) => [group.id.value, group]));
    const sourcesById = new Map(sourceAssetState.sourceStore.sources.map((source) => [source.id.value, source]));
    const playlistsById = new Map(
        sourceAssetState.playlistStore.playlists.map((playlist) => [playlist.id.value, playlist]),
    );

    const rootPermissions = cloneClearancePermissions(folder.userfulSecurityData?.clearanceLevelPermissions ?? []);
    const queue: Array<{ group: CompGroup; permissions: ClearanceLevelID[] }> = [
        { group: folder, permissions: rootPermissions },
    ];
    const processedChildren = new Set<string>();

    while (queue.length > 0) {
        const { group, permissions } = queue.shift()!;
        if (!group?.comps?.length) {
            continue;
        }

        for (const compId of group.comps) {
            const id = compId?.value;
            if (!id || processedChildren.has(id)) {
                continue;
            }

            processedChildren.add(id);

            const childGroup = getChildById(groupsById, id);
            if (childGroup) {
                const updatedChildGroup = buildGroupUpdate(childGroup, permissions);
                requestUpdateGroup(updatedChildGroup);
                groupsById.set(id, updatedChildGroup);
                queue.push({
                    group: updatedChildGroup,
                    permissions: cloneClearancePermissions(
                        updatedChildGroup.userfulSecurityData.clearanceLevelPermissions,
                    ),
                });
                continue;
            }

            const childSource = getChildById(sourcesById, id);
            if (childSource) {
                const updatedSource = buildSourceUpdate(childSource, permissions);
                sourceAssetStore.dispatch(sourceSliceActions.forceUpdateSourceToServer(updatedSource));
                continue;
            }

            const childPlaylist = getChildById(playlistsById, id);
            if (childPlaylist) {
                const updatedPlaylist = buildPlaylistUpdate(childPlaylist, permissions);
                sourceAssetStore.dispatch(playlistSliceActions.forceUpdatePlaylistToServer(updatedPlaylist));
                continue;
            }
        }
    }
};
